import { useState, useEffect } from 'react';
import { GeneralManagerDashboard } from './GeneralManagerDashboard';
import { ZoneManagerDashboard } from './ZoneManagerDashboard';
import { RepresentativeDashboard } from './RepresentativeDashboard';
import { apiClient } from '../../utils/apiClient';

interface User {
  id: string;
  name: string;
  role: string;
}

export const RoleDashboardRouter: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [zoneId, setZoneId] = useState<string | null>(null);
  const [representativeId, setRepresentativeId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        setLoading(true);

        // Obtener usuario del localStorage
        const userStr = localStorage.getItem('user');
        if (!userStr) {
          throw new Error('No se encontró información del usuario');
        }

        const userData = JSON.parse(userStr) as User;
        setUser(userData);

        // Si es ZONE_MANAGER, obtener su zona
        if (userData.role === 'ZONE_MANAGER') {
          try {
            const userZones = await apiClient.get<Array<{ zoneId: string }>>(
              `/admin/users/${userData.id}/zones`
            );
            if (userZones && userZones.length > 0) {
              setZoneId(userZones[0].zoneId);
            } else {
              throw new Error('Zone Manager sin zona asignada');
            }
          } catch (err) {
            console.warn('No se pudo obtener la zona del usuario:', err);
            setError('No se pudo cargar tu zona asignada');
          }
        }

        // Si es REPRESENTATIVE, obtener su ID de representative
        if (userData.role === 'REPRESENTATIVE') {
          try {
            const representative = await apiClient.get<{ id: string }>(
              `/admin/representatives/by-user/${userData.id}`
            );
            if (representative && representative.id) {
              setRepresentativeId(representative.id);
            } else {
              throw new Error('Representative sin ID válido');
            }
          } catch (err) {
            console.warn('No se pudo obtener el representative ID:', err);
            setError('No se pudo cargar tu información de representante');
          }
        }
      } catch (err) {
        console.error('Error loading user data:', err);
        setError('Error al cargar datos del usuario');
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg">
        <div className="font-medium mb-2">Error</div>
        <div>{error}</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-6 py-4 rounded-lg">
        No se encontró información del usuario
      </div>
    );
  }

  // Renderizar dashboard según rol
  switch (user.role) {
    case 'GENERAL_MANAGER':
    case 'SYSTEM_ADMIN':
    case 'ADMIN':
    case 'MKT':
      return <GeneralManagerDashboard />;

    case 'ZONE_MANAGER':
      if (!zoneId) {
        return (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-6 py-4 rounded-lg">
            No se encontró tu zona asignada. Contacta al administrador.
          </div>
        );
      }
      return <ZoneManagerDashboard zoneId={zoneId} />;

    case 'REPRESENTATIVE':
      if (!representativeId) {
        return (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-6 py-4 rounded-lg">
            No se encontró tu información de representante. Contacta al
            administrador.
          </div>
        );
      }
      return <RepresentativeDashboard representativeId={representativeId} />;

    default:
      return (
        <div className="bg-gray-50 border border-gray-200 text-gray-700 px-6 py-4 rounded-lg">
          <div className="font-medium mb-2">Dashboard no disponible</div>
          <div>Tu rol ({user.role}) no tiene un dashboard asignado.</div>
        </div>
      );
  }
};
